import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Column } from '@appkit4/react-components/table';
import { Pagination } from '@appkit4/react-components/pagination';
import './ExtractionSettings.scss';
import { AppRoutes } from '../../../Constants';
import DeleteModal from '../../../components/shared/deleteModal/DeleteModal';
import { Button } from '@appkit4/react-components/button';
import axios from 'axios';
import { useAuth } from 'react-oidc-context';
import {
    useGetV1DocumentTypeByProcessTypeProcessTypeId,
    usePostV1TemplatesGetTemplates,
    usePostV1TemplatesDeactivateTemplate,
    useGetV1TemplatesDownloadExportTemplateHook,
} from '../../../api/generated/endpoint';
import useToast from '../../../helpers/useToast';
import { DeactivateExtractionTemplateRequest } from '../../../api/generated/models';
import CreateTemplateModal from '../../../components/shared/createTemplateModal/CreateTemplateModal';

const ExtractionSettings = () => {
    const [exportTemplatesPage, setExportTemplatesPage] = useState(1);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<number | null>(
        null
    );
    const [createTemplateModalVisible, setCreateTemplateModalVisible] =
        useState(false);
    const [availableDocTypes, setAvailableDocTypes] = useState<any[]>([]);
    const [exportTemplatesData, setExportTemplatesData] = useState<any[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [totalTemplatesCount, setTotalTemplatesCount] = useState(0);

    const pageSize = 7;
    const navigate = useNavigate();
    const showToast = useToast();
    const auth = useAuth();

    const { data: documentTypesData, isLoading: isLoadingDocTypes } =
        useGetV1DocumentTypeByProcessTypeProcessTypeId(1);

    const templatesGetMutation = usePostV1TemplatesGetTemplates();
    const deactivateTemplateMutation = usePostV1TemplatesDeactivateTemplate();

    const getV1TemplatesDownloadExportTemplate =
        useGetV1TemplatesDownloadExportTemplateHook();

    const fetchExportTemplates = () => {
        setIsLoadingTemplates(true);
        templatesGetMutation.mutate(
            {
                data: {
                    documentTypeIds: [],
                },
            },
            {
                onSuccess: (response) => {
                    if (response) {
                        setExportTemplatesData(response);
                        setTotalTemplatesCount(response.length);
                    }
                    setIsLoadingTemplates(false);
                },
                onError: (error) => {
                    console.error('Error fetching export templates:', error);
                    showToast(
                        'Error',
                        'Failed to fetch export templates. Please try again.',
                        'error'
                    );
                    setIsLoadingTemplates(false);
                },
            }
        );
    };

    useEffect(() => {
        fetchExportTemplates();
    }, [exportTemplatesPage]);

    useEffect(() => {
        if (documentTypesData) {
            setAvailableDocTypes(documentTypesData);
        }
    }, [documentTypesData]);

    const handleExportTemplatesPageChange = (page: number) => {
        setExportTemplatesPage(page);
    };

    const handleDeleteTemplate = (id: number) => {
        setTemplateToDelete(id);
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (templateToDelete !== null) {
            try {
                await deactivateTemplateMutation.mutateAsync({
                    data: {
                        templateId: templateToDelete,
                    } as DeactivateExtractionTemplateRequest,
                });

                showToast(
                    'Success',
                    'Template deleted successfully',
                    'success'
                );

                setDeleteModalVisible(false);
                setTemplateToDelete(null);

                // Add back manual refetch
                fetchExportTemplates();
            } catch (error: any) {
                showToast(
                    'Error',
                    'Failed to delete template. Please try again.',
                    'error'
                );
            }
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setTemplateToDelete(null);
    };

    const templateToDeleteName =
        exportTemplatesData?.find(
            (template) => template.templateId === templateToDelete
        )?.templateName || '';

    const handleCreateTemplate = () => {
        setCreateTemplateModalVisible(true);
    };

    const handleCloseCreateTemplateModal = () => {
        setCreateTemplateModalVisible(false);
    };

    const handleConfirmCreateTemplate = async (
        templateName: string,
        selectedDocTypes: any[],
        uploadedFile: any
    ) => {
        if (!templateName || selectedDocTypes.length === 0 || !uploadedFile) {
            return;
        }

        try {
            const actualFile = uploadedFile.originFile || uploadedFile;

            if (!(actualFile instanceof Blob)) {
                showToast(
                    'Error',
                    'Invalid file format. Please upload a valid Excel file.',
                    'error'
                );
                return;
            }

            const formData = new FormData();
            formData.append('ExcelFile', actualFile);
            formData.append('TemplateName', templateName);

            selectedDocTypes.forEach((docType) => {
                formData.append('DocumentTypeIds', docType.id.toString());
            });

            // for (const pair of formData.entries()) {
            //     console.log(pair[0], pair[1]);
            // }

            const token = auth.user?.id_token;

            if (!token) {
                throw new Error('No ID token available');
            }

            await axios.post('/v1/Templates/UploadExportTemplate', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                baseURL: process.env.REACT_APP_API_BASE_URL || '',
            });

            showToast('Success', 'Template created successfully', 'success');

            // Add back manual refetch
            fetchExportTemplates();

            setCreateTemplateModalVisible(false);
        } catch (error: any) {
            showToast(
                'Error',
                `Failed to create template: ${
                    error.message || 'Unknown error'
                }`,
                'error'
            );
        }
    };

    const handleDownloadTemplate = (id: number) => {
        getV1TemplatesDownloadExportTemplate({ templateId: id })
            .then((data) => {
                if (data && data.exportTemplateSASUrl) {
                    const template = exportTemplatesData.find(
                        (t) => t.templateId === id
                    );
                    const fileName = template
                        ? `${template.templateName}.xlsx`
                        : 'template.xlsx';

                    const downloadLink = document.createElement('a');
                    downloadLink.href = data.exportTemplateSASUrl;

                    downloadLink.setAttribute('download', fileName);

                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);

                    showToast(
                        'Success',
                        'Template downloaded successfully',
                        'success'
                    );
                } else {
                    showToast(
                        'Error',
                        'Failed to download template. No URL returned.',
                        'error'
                    );
                }
            })
            .catch((error) => {
                console.error('Error downloading template:', error);
                showToast(
                    'Error',
                    'Failed to download template. Please try again.',
                    'error'
                );
            });
    };

    const renderingCell = (row: any, field: string) => {
        return (
            <span
                role="button"
                onClick={() =>
                    navigate(
                        `${AppRoutes.ExtractionDocTypeSettings}?docType=${row.documentTypeName}&docTypeId=${row.documentTypeId}&processTypeId=1`
                    )
                }
                aria-label="Settings"
            >
                {row[field]}
            </span>
        );
    };

    return (
        <div className="extraction-settings">
            <h2 className="ap-typography-heading-m title">
                Extraction Document Types
            </h2>
            <div className="table-container">
                {availableDocTypes.length > 0 ? (
                    <>
                        <Table
                            className="extraction-table"
                            originalData={availableDocTypes}
                            pageSize={pageSize}
                            hasTitle
                        >
                            <Column
                                field="documentTypeName"
                                renderCell={renderingCell}
                            >
                                Document type
                            </Column>
                            <Column
                                field="documentTypeId"
                                renderCell={renderingCell}
                            >
                                Document type id
                            </Column>
                            <Column
                                field="documentTypeName"
                                style={{ width: '50px' }}
                                renderCell={(row: any) => (
                                    <span
                                        style={{ color: '#BFBFBF' }}
                                        className="Appkit4-icon icon-enter-right-outline action-icon"
                                        role="button"
                                        onClick={() =>
                                            navigate(
                                                `${AppRoutes.ExtractionDocTypeSettings}?docType=${row.documentTypeName}&docTypeId=${row.documentTypeId}&processTypeId=1`
                                            )
                                        }
                                        aria-label="Settings"
                                    />
                                )}
                            ></Column>
                        </Table>
                    </>
                ) : (
                    <div>No document types yet</div>
                )}
            </div>

            <h2
                className="ap-typography-heading-m title"
                style={{ marginTop: '40px' }}
            >
                Export templates
            </h2>
            <div className="table-container">
                {isLoadingTemplates ? (
                    <div>Loading templates...</div>
                ) : (
                    <>
                        <Table
                            className="export-templates-table"
                            originalData={exportTemplatesData || []}
                            currentPage={exportTemplatesPage}
                            pageSize={pageSize}
                            hasTitle
                        >
                            <Column field="templateName">Template name</Column>
                            <Column
                                field="documentTypeNames"
                                renderCell={(row: any) => (
                                    <span>
                                        {row.documentTypeNames.join(', ')}
                                    </span>
                                )}
                            >
                                Document types
                            </Column>
                            <Column
                                field="templateId"
                                style={{ width: '100px' }}
                                renderCell={(row: any) => (
                                    <div className="action-icons-container">
                                        <span
                                            style={{
                                                color: '#BFBFBF',
                                                marginRight: '16px',
                                            }}
                                            className="Appkit4-icon icon-download-outline action-icon"
                                            role="button"
                                            onClick={() =>
                                                handleDownloadTemplate(
                                                    row.templateId
                                                )
                                            }
                                            aria-label="Download"
                                        />
                                        <span
                                            style={{ color: '#BFBFBF' }}
                                            className="Appkit4-icon icon-delete-outline action-icon"
                                            role="button"
                                            onClick={() =>
                                                handleDeleteTemplate(
                                                    row.templateId
                                                )
                                            }
                                            aria-label="Delete"
                                        />
                                    </div>
                                )}
                            ></Column>
                        </Table>
                        <Pagination
                            total={Math.ceil(totalTemplatesCount / pageSize)}
                            current={exportTemplatesPage}
                            onPageChange={handleExportTemplatesPageChange}
                            style={{ float: 'right' }}
                        />
                    </>
                )}
            </div>

            <Button
                className="create-template-button"
                icon="icon-circle-plus-outline"
                onClick={handleCreateTemplate}
                style={{ marginTop: '20px' }}
            >
                Create template
            </Button>

            <DeleteModal
                visible={deleteModalVisible}
                onClose={handleCancelDelete}
                title="Delete Export Template"
                content={
                    <div>
                        <p>
                            Are you sure you want to delete the export template
                            &quot;{templateToDeleteName}&quot;?
                        </p>
                        <p>This action cannot be undone.</p>
                        <div className="delete-modal-actions">
                            <button
                                className="ap-button ap-button-secondary"
                                onClick={handleCancelDelete}
                            >
                                Cancel
                            </button>
                            <button
                                className="ap-button ap-button-primary ap-button-danger"
                                onClick={handleConfirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                }
            />
            {availableDocTypes.length && (
                <CreateTemplateModal
                    visible={createTemplateModalVisible}
                    onClose={handleCloseCreateTemplateModal}
                    onConfirm={handleConfirmCreateTemplate}
                    documentTypes={availableDocTypes}
                    isLoadingDocTypes={isLoadingDocTypes}
                />
            )}
        </div>
    );
};

export default ExtractionSettings;
